import axios from 'axios'
import { showToast } from 'vant'
import Cookies from 'js-cookie'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import { useUserStore } from '@/store'
import { useRouter } from 'vue-router'
import { Toast } from 'vant'
import { getLoginPathForSessionExpiry } from '@/utils/loginRedirect'
import { shouldApiCrypto, wrapApiRequestBody, wrapApiRequestParams, unwrapApiResponse } from '@/utils/apiCrypto'

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API, // 使用环境变量中的API地址
  timeout: 15000 // 请求超时时间
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 与若依管理端一致：headers.isToken === false 时不携带 Authorization
    const skipToken = (config.headers || {}).isToken === false

    const whiteList = [
      '/hz/wx/oauth/getAuthUrl',
      '/hz/wx/oauth/getAccessToken',
      '/hz/wx/oauth/getUserInfo',
      '/hz/wx/user/login',
      '/hz/wx/user/loginByUserName',
      '/hz/wx/user/loginActivityDashboardOnly',
      '/hz/idm/oauth/enabled',
      '/hz/idm/oauth/url',
      '/hz/idm/oauth/unified-login',
      '/qb/unauth/entra/login'
    ]

    const rawPath = (config.url || '').split('?')[0]
    const isWhiteListUrl = whiteList.some((item) => {
      if (item === '/hz/wx/user/login') {
        return rawPath === '/hz/wx/user/login' || rawPath.endsWith('/hz/wx/user/login')
      }
      return rawPath === item || rawPath.endsWith(item) || (config.url || '').includes(item)
    })

    const shouldAttachToken = !skipToken && !isWhiteListUrl

    if (shouldAttachToken) {
      const userStore = useUserStore()
      const token = userStore.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : '') || ''
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      } else {
        console.warn('Token不存在，可能需要重新登录:', config.url)
      }
    }

    if (config.params != null && Object.keys(config.params).length > 0) {
      config.params = wrapApiRequestParams(config.url, config.params, config.method)
    }

    if (config.data != null) {
      config.data = wrapApiRequestBody(config.url, config.data, config.method)
    }
    
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// response拦截器
service.interceptors.response.use(
  response => {
    NProgress.done()
    if (response.config && response.config.responseType === 'blob') {
      return response
    }
    const res = response.data
    const decrypted = unwrapApiResponse(response.config?.url, res)
    
    // 如果返回的状态码表示 token 失效（通常是 401 或特定的业务码）
    if (decrypted.code === 401 || decrypted.code === 10001) { // 根据您的 API 调整具体的失效码
      // 清除本地存储的 token
      localStorage.removeItem('token')
      
      // 使用正确的 Toast 方法
      showToast('登录已过期，请重新登录')
      
      // 跳转到登录页面（大屏与业务分流）
      window.location.href = getLoginPathForSessionExpiry()

      return Promise.reject(new Error('登录已过期，请重新登录'))
    }

  if (decrypted.code === 500 || decrypted.code === 10001) { // 根据您的 API 调整具体的失效码
      // 清除本地存储的 token
      localStorage.removeItem('token')

      // 使用正确的 Toast 方法
      showToast('系统错误，请重新登录')

      // 跳转到登录页面（大屏与业务分流）
      window.location.href = getLoginPathForSessionExpiry()

      return Promise.reject(new Error('系统错误，请重新登录'))
  }
    
    // 根据自己的业务逻辑修改
    if (decrypted.code === 200) {
      // 如果响应中包含用户信息，更新用户信息
      if (decrypted.data && decrypted.data.userInfo) {
        const userStore = useUserStore()
        userStore.updateUserInfo(decrypted.data.userInfo)
      }
      return decrypted
    } else {
      showToast({
        message: decrypted.msg || '请求失败',
        type: 'fail'
      })
      return Promise.reject(new Error(decrypted.msg || '请求失败'))
    }
  },
  error => {
    NProgress.done()
    console.error('请求错误', error)
    
    // 处理 HTTP 状态码错误
    if (error.response) {
      console.error('错误状态码:', error.response.status)
      console.error('错误数据:', error.response.data)
      
      // 处理 HTTP 状态码错误
      if (error.response.status === 401) {
        // 清除本地存储的 token
        localStorage.removeItem('token')
        
        // 提示用户
        showToast('登录已过期，请重新登录')
        
        // 跳转到登录页面（大屏与业务分流）
        window.location.href = getLoginPathForSessionExpiry()

        return Promise.reject(error)
      }
    }
    
    showToast({
      message: error.message || '请求失败',
      type: 'fail'
    })
    return Promise.reject(error)
  }
)

export default service 